import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Icon from '../Icon';
import { IconEnum as Icons } from '../Icons';
import { AppContext } from '../../context/AppContext';
import colors from '../../styles/colors';
import NoContent from '../../shared/NoContent';
import { getDateByOffset } from '../../util/Time';
import BaseModal, { ModalList } from '../modals/BaseModal';
import useMutateAnalytics from '../../util/mutate.Analytics';
import analyticsKeys from '../../util/UsageStatLinks';

const TableHeaderComponent = ({ ordering, ...props }) => {
  const { fieldName, order } = ordering;
  let orderingIcon = (
    <Icon
      icon={Icons.Sort}
      size={12}
      margin="5px 0 0 0"
      active
      activeColor={colors.grey3}
    />
  );

  if (fieldName === props.columnName && order === 'asc') {
    orderingIcon = (
      <Icon
        margin="5px 0 0 0"
        icon={Icons.SortUp}
        size={12}
        active
        activeColor={colors.grey3}
      />
    );
  } else if (fieldName === props.columnName && order === 'desc') {
    orderingIcon = (
      <Icon
        margin="5px 0 0 0"
        icon={Icons.SortDown}
        size={12}
        active
        activeColor={colors.grey3}
      />
    );
  }

  return (
    <TableRowItem
      header
      minWidth={props.minWidth || '180px'}
      onClick={() => props.onClick(props.columnName) || null}
    >
      {props.children} {orderingIcon}
    </TableRowItem>
  );
};

const TableContainer = styled.div`
  background: ${colors.white};
  width: 100%;
  height: 60vh;
  overflow: auto;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  border-radius: 0px 10px 10px 0px;
  border: 1px solid ${colors.grey6};
  position: relative;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 10px;
  }

  @media screen and (max-width: 991px) {
    width: 100%;
  }
`;

const TableRow = styled.div`
  transition: all 0.1s;
  align-items: center;
  display: table;
  min-height: 40px;
  &:hover {
    background: rgba(211, 211, 211, 0.1);
  }
`;

const TableHeader = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 500;
  transition: all 0.1s;
  align-items: center;
  display: table;
  min-height: 40px;
  &:hover {
    background: rgba(211, 211, 211, 0.1);
  }
`;

const TableRowItem = styled.div`
  color: ${colors.grey3};
  border-bottom: 1px solid ${colors.grey5};
  border-right: 1px solid ${colors.grey5};
  font-size: 1.3rem;
  width: ${(props) => props.minWidth || '130px'};
  min-width: ${(props) => props.minWidth || '130px'};
  background: ${colors.white};
  padding-right: 10px;
  height: 100%;
  display: table-cell;
  word-break: break-word;
  padding: 10px 20px;
  cursor: ${(props) => (props?.header ? 'pointer' : 'unset')};
  &:first-child {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 400;
    padding: 10px 20px;
    justify-content: space-between;
  }
`;

const NameDataContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  @media screen and (max-width: 600px) {
    flex-direction: row-reverse;
  }
`;

const IconContainer = styled.div`
  margin-left: 10px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  flex-direction: row;
`;

const campaignStatus = Object.freeze({
  Draft: 'Draft',
  Scheduled: 'Scheduled',
  Sent: 'Sent',
  InProgress: 'In Progress',
});

const executionStatus = Object.freeze({
  Pending: 'Pending',
  Processing: 'Processing',
  Completed: 'Completed',
  Failed: 'Failed',
});

export default function CampaignTable({ campaigns, campaignType }) {
  const { app, setApp } = useContext(AppContext);
  const history = useHistory();
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  const [ordering, setOrdering] = useState({
    fieldName: 'dateTime',
    order: 'desc',
  });
  const [allCampaigns, setAllCampaigns] = useState([]);
  const mutateAnalytics = useMutateAnalytics();

  const viewItemClick = (campaignId) => {
    setCampaignId(campaignId);
    setDetailsModalVisible(true);
  };

  // Perform sorting client side
  const dynamicsort = (property, order) => {
    let sort_order = 1;
    if (order === 'desc') {
      sort_order = -1;
    }
    return function (a, b) {
      // a should come before b in the sorted order
      if (a[property] < b[property]) {
        return -1 * sort_order;
        // a should come after b in the sorted order
      }
      if (a[property] > b[property]) {
        return 1 * sort_order;
        // a and b are the same
      }
      return 0 * sort_order;
    };
  };

  const handleSort = (columnName) => {
    const newOrderDirection = ordering.order === 'asc' ? 'desc' : 'asc';
    const shortedCampaigns = allCampaigns.sort(
      dynamicsort(columnName, newOrderDirection),
    );
    setOrdering({ fieldName: columnName, order: newOrderDirection });
    setAllCampaigns(shortedCampaigns);
  };

  useEffect(() => {
    setAllCampaigns(campaigns);
  }, [campaigns]);

  return (
    <>
      <BaseModal
        open={detailsModalVisible}
        modalType={ModalList.CampaignDetailsModal}
        onClose={setDetailsModalVisible}
        campaignId={campaignId}
      />
      <TableContainer>
        {allCampaigns && allCampaigns.length > 0 ? (
          <>
            <TableHeader>
              <TableHeaderComponent
                columnName="name"
                ordering={ordering}
                onClick={handleSort}
                minWidth="250px"
              >
                CAMPAIGN NAME
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="status"
                ordering={ordering}
                onClick={handleSort}
                minWidth="180px"
              >
                STATUS
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="dateTime"
                ordering={ordering}
                onClick={handleSort}
                minWidth="180px"
              >
                DATE-TIME
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="text"
                ordering={ordering}
                onClick={handleSort}
                minWidth="400px"
              >
                MESSAGE
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="tags"
                ordering={ordering}
                onClick={handleSort}
                minWidth="180px"
              >
                TAGS
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="numberSent"
                ordering={ordering}
                onClick={handleSort}
                minWidth="150px"
              >
                NUMBER SENT
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="totalUrlClicks"
                ordering={ordering}
                onClick={handleSort}
                minWidth="150px"
              >
                CLICK-THRUS / CONVERSIONS
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="creditsUsed"
                ordering={ordering}
                onClick={handleSort}
                minWidth="140px"
              >
                CREDIT USED
              </TableHeaderComponent>
              <TableHeaderComponent
                columnName="optOuts"
                ordering={ordering}
                onClick={handleSort}
                minWidth="140px"
              >
                OPT-OUTs
              </TableHeaderComponent>
            </TableHeader>
            {allCampaigns.map((campaign) => {
              const date = getDateByOffset(
                app.orgTimeZoneOffset,
                new Date(campaign.dateTime * 1000),
              );
              const format = 'MM/DD/YYYY hh:mm A';
              const convertedDate = moment(date).format(format);

              let status = campaign.status;
              let showEdit = false;
              if (campaign?.isTriggerCampaign) {
                showEdit = true;
              } else {
                if (
                  campaign.status === campaignStatus.Scheduled
                  || campaign.status === campaignStatus.Draft
                ) {
                  showEdit = true;
                }
                if (campaign.status === campaignStatus.Sent) {
                  if (
                    campaign.executionStatus === executionStatus.Pending
                    || campaign.executionStatus === executionStatus.Processing
                  ) {
                    status = campaignStatus.InProgress;
                  }
                }
              }

              return (
                <TableRow key={campaign._id}>
                  <TableRowItem minWidth="250px">
                    <NameDataContainer>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        {campaign.name}
                      </div>
                      <IconContainer>
                        {showEdit && (
                          <div data-tip="Edit this Campaign">
                            <Icon
                              icon={Icons.EditRegular}
                              onClick={() => {
                                history.push({
                                  pathname: `/campaigns/id=${campaign._id}`,
                                  search: `?type=${campaignType}`,
                                  state: { campaignType },
                                });

                                const mutateAnalyticsVariables = {
                                  link: analyticsKeys.CAMPAIGNS_EDIT_BUTTON,
                                };
                                mutateAnalytics(mutateAnalyticsVariables);
                              }}
                              size={18}
                              active
                            />
                          </div>
                        )}
                        {campaign.executionStatus
                          === executionStatus.Completed && (
                          <div data-tip="View Details">
                            <Icon
                              icon={Icons.EyeRegular}
                              onClick={() => viewItemClick(campaign._id)}
                              size={18}
                              active
                            />
                          </div>
                        )}
                        <div data-tip="Replicate this Campaign">
                          <Icon
                            icon={Icons.CopyRegular}
                            size={18}
                            active
                            onClick={() => {
                              history.push({
                                pathname: '/campaigns/new',
                                search: `?type=${campaignType}&id=${campaign._id}`,
                                state: { campaignType },
                              });

                              const mutateAnalyticsVariables = {
                                link: analyticsKeys.CAMPAIGNS_REPLICATE_BUTTON,
                              };
                              mutateAnalytics(mutateAnalyticsVariables);

                              setApp((app) => ({
                                ...app,
                                campaignId: campaign._id,
                              }));
                            }}
                          />
                        </div>
                      </IconContainer>
                    </NameDataContainer>
                  </TableRowItem>
                  <TableRowItem minWidth="180px">{campaign?.isTriggerCampaign && status === 'Sent' ? 'Active' : status}</TableRowItem>
                  <TableRowItem minWidth="180px">
                    {campaign.status !== campaignStatus.Draft
                      ? convertedDate
                      : 'Drafted'}
                  </TableRowItem>

                  <TableRowItem minWidth="400px">{campaign.text}</TableRowItem>
                  <TableRowItem minWidth="180px">
                    {Array.isArray(campaign.tags) && campaign.tags.length > 0
                      ? campaign.tags.join(', ')
                      : ''}
                  </TableRowItem>
                  <TableRowItem minWidth="150px">
                    {campaign?.numberSent}
                  </TableRowItem>
                  <TableRowItem minWidth="150px">
                    {campaign?.totalUrlClicks}
                  </TableRowItem>
                  <TableRowItem minWidth="140px">
                    {campaign?.creditsUsed}
                  </TableRowItem>
                  <TableRowItem minWidth="140px">
                    {campaign?.optOuts}
                  </TableRowItem>
                </TableRow>
              );
            })}
          </>
        ) : (
          <NoContent
            header="No Campaigns"
            description='No campaigns yet. Create one by clicking the "Create Campaign" button.'
          />
        )}
      </TableContainer>
      <ReactTooltip
        place="right"
        type="dark"
        effect="solid"
        clickable={false}
      />
    </>
  );
}
